import { Box, Paper, Typography, Autocomplete, TextField, Backdrop, CircularProgress, Button, InputAdornment, Chip, Divider } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import RaiseTicketVM from "../viewmodel/RaiseTicketVM";
import { GetTools } from "../model/generalApiCalls";
import { useToast } from "../../basecomp/components/provider/ToastProvider";

// Vibrant Filled Icons
import ArticleIcon from '@mui/icons-material/Article';
import GridViewIcon from '@mui/icons-material/GridView';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// CONAS Brand Colors
const CONAS_BLUE = "#163672";

const FormLabel = ({ text, required }) => (
    <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", mb: 0.5, fontSize: "0.85rem", fontFamily: "'Inter', sans-serif" }}>
        {text} {required && <span style={{ color: "#ef4444" }}>*</span>}
    </Typography>
);

function RaiseTicketUser() {
    const [ticketHeading, setTicketHeading] = useState("")
    const [ticketDescription, setTicketDescription] = useState("")
    const [tickettool, setTicketTool] = useState(null)
    const [ticketType, setTicketType] = useState(null)
    const [ticketfiles, setFiles] = useState([]) 
    const [toolsData, setToolsData] = useState([])
    const [loading, setLoading] = useState(false)
    const { handleToastOpen } = useToast()
    const fileRef = useRef(null);

    const handelTicketSubmit = async () => {
        if (!ticketHeading || !ticketDescription || !tickettool || !ticketType) {
            handleToastOpen("error", "Please fill in all required fields.")
            return
        }
        setLoading(true)
        try {
            let response = await RaiseTicketVM(ticketHeading, ticketDescription, tickettool, ticketfiles, ticketType)
            if (response.status === 400) {
                handleToastOpen("error", response.response.data.message)
                setLoading(false)
            }
            if (response.status === 200) {
                handleReset()
                handleToastOpen("success", "Ticket Submitted Successfully")
            }
        } catch (error) {
            handleToastOpen("error", error.message || "An error occurred");
        } finally {
            setLoading(false)
        }
    }

    const handleReset = () => {
        setTicketHeading(""); setTicketDescription(""); setTicketTool(null); setTicketType(null); setFiles([]);
        if (fileRef.current) fileRef.current.value = "";
    }

    const handleRemoveFile = (indexToRemove) => {
        setFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    useEffect(() => {
        const getTools = async () => {
            try {
                let response = await GetTools()
                let newData = Array.from(response.data).map((elem) => elem.title)
                setToolsData(newData)
            } catch (error) {
                handleToastOpen("error", "Something went wrong fetching tools")
            }
        }
        getTools()
    }, [handleToastOpen])

    // Modern SaaS Input Style
    const modernInputStyle = {
        "& .MuiOutlinedInput-root": { 
            borderRadius: "8px", 
            backgroundColor: "#f8fafc", 
            transition: "all 0.2s ease",
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: "#cbd5e1" },
            "&.Mui-focused": { backgroundColor: "#ffffff" },
            "&.Mui-focused fieldset": { borderColor: CONAS_BLUE, borderWidth: "2px" } 
        }
    };

    return (
        <Box sx={{ 
            width: "100%", 
            height: "100%", // Fill parent height completely
            boxSizing: "border-box", 
            display: "flex", 
            justifyContent: "center", 
            alignItems: "stretch", // Stretch to fill container
            fontFamily: "'Inter', sans-serif",
            '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiChip-root': {
                fontFamily: "'Inter', sans-serif",
            }
        }}>
            
            {/* Form Container - Locked Height, No Scroll */}
            <Paper elevation={0} sx={{ 
                width: "100%", 
                maxWidth: "1100px", 
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: { xs: "16px", md: "20px" }, 
                backgroundColor: "#ffffff",
                boxShadow: "0px 10px 40px rgba(0,0,0,0.03)",
                border: "1px solid #e2e8f0",
                overflow: "hidden" // Prevents any internal scrolling
            }}>
                
                {/* Header Area - Fixed Height */}
                <Box sx={{ px: { xs: 3, md: 4 }, py: 2.5, backgroundColor: "#ffffff", flexShrink: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5, letterSpacing: "-0.02em" }}>
                        Raise a New Ticket
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Provide detailed information so our technical team can assist you faster.
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: "#f1f5f9" }} />

                {/* Body Area - Side-by-Side Grid to save vertical space */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 4 }, p: { xs: 3, md: 4 }, flexGrow: 1, minHeight: 0 }}>
                    
                    {/* LEFT COLUMN: Classification & Brief Info */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        
                        <Box>
                            <FormLabel text="Subject / Heading" required />
                            <TextField fullWidth size="small" placeholder="Briefly summarize the issue..."
                                value={ticketHeading} onChange={(e) => { setTicketHeading(e.target.value.slice(0, 300)) }}
                                sx={modernInputStyle}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ArticleIcon sx={{ color: "#94a3b8", fontSize: 18, ml: 0.5 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                            <Box>
                                <FormLabel text="Affected Tool" required />
                                <Autocomplete size="small" options={toolsData || []} value={tickettool} onChange={(event, newValue) => setTicketTool(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select Tool..." sx={modernInputStyle} 
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (<><InputAdornment position="start"><GridViewIcon sx={{ color: "#94a3b8", fontSize: 18, ml: 0.5 }} /></InputAdornment>{params.InputProps.startAdornment}</>)
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                            <Box>
                                <FormLabel text="Request Type" required />
                                <Autocomplete size="small" options={["Improvement", "Bug", "IT Tool Access"]} value={ticketType} onChange={(event, newValue) => setTicketType(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select Type..." sx={modernInputStyle} 
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: (<><InputAdornment position="start"><LocalOfferIcon sx={{ color: "#94a3b8", fontSize: 18, ml: 0.5 }} /></InputAdornment>{params.InputProps.startAdornment}</>)
                                            }}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                            <FormLabel text="Detailed Description" required />
                            <TextField fullWidth multiline rows={4} placeholder="Provide steps to reproduce the issue..."
                                value={ticketDescription} onChange={(e) => { setTicketDescription(e.target.value.slice(0, 1000)) }}
                                sx={{ ...modernInputStyle, flexGrow: 1, "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ mt: 1.5 }}>
                                            <EditIcon sx={{ color: "#94a3b8", fontSize: 18, ml: 0.5 }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Typography variant="caption" sx={{ display: "block", textAlign: "right", color: "#9ca3af", mt: 0.5 }}>
                                {ticketDescription.length}/1000
                            </Typography>
                        </Box>
                    </Box>

                    {/* RIGHT COLUMN: Attachments Area */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
                        <FormLabel text="Supporting Evidence (Screenshots/Videos)" />
                        
                        <input type="file" multiple ref={fileRef} id="inputfile" accept="image/*,video/*" style={{ display: "none" }}
                            onChange={(e) => {
                                const MAX_SIZE = 50 * 1024 * 1024;
                                const newFiles = Array.from(e.target.files);
                                const validFiles = newFiles.filter((file) =>
                                    (file.type.startsWith("image/") || file.type.startsWith("video/")) && file.size <= MAX_SIZE
                                );

                                if (validFiles.length !== newFiles.length) {
                                    handleToastOpen("error", "Some files rejected. Images/videos under 50MB only.");
                                }

                                setFiles(prev => [...prev, ...validFiles]);
                                if (fileRef.current) fileRef.current.value = "";
                            }}
                        />

                        {/* Dropzone fills remaining height dynamically */}
                        <label htmlFor="inputfile" style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                            <Box sx={{ 
                                flexGrow: 1, border: "2px dashed #cbd5e1", borderRadius: "12px", 
                                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                cursor: "pointer", transition: "all 0.2s ease", backgroundColor: "#f8fafc",
                                minHeight: "120px",
                                "&:hover": { borderColor: CONAS_BLUE, backgroundColor: "#f1f5f9" }
                            }}>
                                <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: "#94a3b8", mb: 0.5 }} />
                                <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
                                    Click or drag files here
                                </Typography>
                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                    Max 50MB (Images & Videos)
                                </Typography>
                            </Box>
                        </label>
                        
                        {/* Chips container */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1, maxHeight: "80px", overflowY: "auto", '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '4px' } }}>
                            {ticketfiles.map((file, index) => (
                                <Chip 
                                    key={`${file.name}-${index}`} 
                                    label={file.name} 
                                    onDelete={() => handleRemoveFile(index)}
                                    deleteIcon={<DeleteOutlineIcon />}
                                    size="small"
                                    sx={{ 
                                        backgroundColor: "#eef2ff", border: "1px solid #c7d2fe", color: CONAS_BLUE, fontWeight: 500, maxWidth: "100%", 
                                        "& .MuiChip-deleteIcon": { color: "#818cf8", "&:hover": { color: "#4f46e5" } }
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                </Box>

                {/* Sticky-Style Footer - Fixed Height */}
                <Box sx={{ 
                    px: { xs: 3, md: 4 }, py: 2, 
                    backgroundColor: "#f8fafc", 
                    borderTop: "1px solid #e2e8f0",
                    display: "flex", justifyContent: "flex-end", gap: 2, alignItems: "center", flexShrink: 0 
                }}>
                    <Button variant="outlined" onClick={handleReset}
                        sx={{ 
                            color: "#64748b", borderColor: "#cbd5e1", 
                            textTransform: "none", fontWeight: 600, px: 3, borderRadius: "8px",
                            "&:hover": { backgroundColor: "#f1f5f9", borderColor: "#94a3b8" } 
                        }}
                    >
                        Clear Form
                    </Button>
                    <Button variant="contained" onClick={handelTicketSubmit}
                        sx={{ 
                            backgroundColor: CONAS_BLUE, color: "#ffffff", 
                            textTransform: "none", fontWeight: 600, px: 4, borderRadius: "8px", boxShadow: "none",
                            "&:hover": { backgroundColor: "#112a59", boxShadow: "0 4px 12px rgba(22, 54, 114, 0.2)" } 
                        }}
                    >
                        Submit Ticket
                    </Button>
                </Box>

            </Paper>

            <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    )
}

export default RaiseTicketUser;